import { Editor } from '@ckeditor/ckeditor5-core';
// eslint-disable-next-line import/no-extraneous-dependencies
// eslint-disable-next-line import/no-extraneous-dependencies
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
// eslint-disable-next-line import/no-extraneous-dependencies
import Text from '@ckeditor/ckeditor5-engine/src/model/text';
// eslint-disable-next-line import/no-extraneous-dependencies
import Writer from '@ckeditor/ckeditor5-engine/src/model/writer';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { v4 as uuidv4 } from 'uuid';
import { ElementDefinition, TagAttributesDefinition } from './Definition';
import Schema from './Schema';

type TextAttributeDialogProps = {
  editor: Editor;
  node: Node;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

interface Attribute {
  name: string;
  value: string;
  parentName: string;
}

interface Tag {
  name: string;
  definition: ElementDefinition;
  attributes: Attribute[];
}

/**
 * テキストの属性編集ダイアログコンポーネントです。
 * @param editor エディタインスタンス。
 * @param node 対象ノード。
 * @param isOpen ダイアログの表示状態。
 * @param setIsOpen ダイアログの表示状態を変更するための関数。
 */
function TextAttributeDialog({ editor, node, isOpen, setIsOpen }: TextAttributeDialogProps) {
  /**
   * tagAttributeとは、以下のような構造になっているmodel要素に付加されているview上の要素に対応した属性を格納しているもの。
   * attributesやclassesは必ずあるとは限らない。
   * [
   *  'htmlDivAttributes',
   *  {
   *   attributes: {},
   *   classes: [],
   *  }
   * ]
   */

  // 要素の取り得る属性定義を取得
  const tagAttributesDefinitions = useMemo(() => {
    if (!node.is('$text')) {
      return [];
    }

    // htmlSpanはすべてのインライン要素が対象
    const attributesDefinitions = [Schema.getTagAttributesDefinition('htmlSpan')] as TagAttributesDefinition[];

    const aAttributes = node.getAttribute('htmlA');
    if (aAttributes) {
      const aAttributesDefinition = Schema.getTagAttributesDefinition('htmlA');
      if (aAttributesDefinition) {
        attributesDefinitions.push(aAttributesDefinition);
      }
    }

    return attributesDefinitions;
  }, [node]);

  /**
   * 要素のhtml??Attributes属性内から指定された属性値を取得する。
   * @param element 対象要素。
   * @param name 属性名。
   * @param attributesName 対象html??Attributes名（例：htmlDivAttributes）
   * @returns 属性値。存在しなくても空文字が返される。
   */
  const getAttributeInAttributes = (text: Text, name: string, tagAttributesName: string): string => {
    // hrefの場合の特殊処理
    if (name === 'href') {
      const linkHref = text.getAttribute('linkHref') as string | undefined;
      return linkHref || '';
    }

    // html??Attributesという名前の属性値を取得
    const tagAttributes = text.getAttribute(tagAttributesName) as any;
    if (!tagAttributes) {
      return '';
    }

    let attributeValue = '' as string | number | boolean | string[] | undefined;
    if ((tagAttributes as Record<string, string | number | boolean>).attributes) {
      attributeValue = tagAttributes.attributes[name];
    }
    if (!attributeValue) {
      attributeValue = '';
    }

    return attributeValue as string;
  };

  const [tags, setTags] = useState<Tag[]>([]);

  /**
   * 編集用属性値をセットする。
   */
  useEffect(() => {
    if (!node) {
      return;
    }

    const text = node as Text;

    setTags(
      tagAttributesDefinitions?.map((tagAttributesDefinition) => {
        const tag = { attributes: [] as Attribute[] } as Tag;

        tag.name = tagAttributesDefinition.view;
        tag.attributes =
          tagAttributesDefinition.editableAttributes?.map((attributeName) => {
            const attribute = {} as Attribute;
            const attributeValue = getAttributeInAttributes(text, attributeName, tagAttributesDefinition.model);

            attribute.name = attributeName;
            attribute.value = attributeValue;
            attribute.parentName = tagAttributesDefinition.model;

            return attribute;
          }) ?? [];

        return tag;
      }) ?? []
    );
  }, [node, tagAttributesDefinitions]);

  /**
   * 属性ダイアログの「適用」ボタンのクリックイベントハンドラ。
   * @param event イベント。
   * @param tagIndex タグインデックス。
   */
  const handleEditAttributeApplyButtonClick = () => {
    if (!node) {
      console.error('対象ノードが指定されていません。');
      return;
    }

    if (!node.is('$text')) {
      console.error('このコンポーネントはテキストノードが対象です。');
      return;
    }

    editor.model.change((writer: Writer) => {
      tags.forEach((tag) => {
        tag.attributes.forEach((attribute) => {
          // hrefの場合の特殊処理
          if (attribute.name === 'href') {
            writer.removeAttribute('linkHref', node);
            writer.setAttribute('linkHref', attribute.value, node);
            return;
          }

          const attributeValue = attribute.value as string | number | boolean | string[];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let attributes = node.getAttribute(attribute.parentName) as any;
          if (!attributes) {
            attributes = {};
          }
          if (!attributes.attributes) {
            attributes.attributes = {} as Record<string, string | number | boolean>;
          }
          if (attributeValue === '') {
            delete attributes.attributes[attribute.name];
          } else {
            attributes.attributes[attribute.name] = attributeValue;
          }

          if (!attributes.classes) {
            attributes.classes = [] as string[];
          }

          // attributes.attributes, attributes.classesが空の場合は属性を削除する
          writer.removeAttribute(attribute.parentName, node);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          if (Object.keys(attributes.attributes).length > 0 || attributes.classes.length > 0) {
            writer.setAttribute(attribute.parentName, attributes as Record<string, string | number | boolean>, node);
          }
        });
      });
    });

    setIsOpen(false);
  };

  /**
   * 属性ダイアログの「キャンセル」ボタンのクリックイベントハンドラ。
   * @param event イベント。
   */
  const handleEditAttributeCancelButtonClick = () => {
    setIsOpen(false);
  };

  /**
   * 属性値が変更されたときのイベントハンドラ。
   * @param event イベント。
   * @param tagIndex タグの中でのインデックス。
   * @param attributeIndex 属性の中でのインデックス。
   */
  const handleOnChangeAttributeValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    tagIndex: number,
    attributeIndex: number
  ) => {
    tags[tagIndex].attributes[attributeIndex].value = event.target.value;
    setTags(tags);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      className="attribute-dialog text-attribute-dialog"
      overlayClassName="attribute-dialog-overlay"
      shouldCloseOnOverlayClick={false}
      ariaHideApp={false}
    >
      {tags &&
        tags.map((tag, tagIndex) => (
          <div key={uuidv4()}>
            <div className="header">
              <strong>{tag.name}</strong>
              {t(' - Attributes')}
            </div>
            <table>
              <tbody>
                {tag.attributes.map((attribute, attributeIndex) => (
                  <tr key={uuidv4()} className="attribute">
                    <td className="name">{attribute.name}</td>
                    <td className="value">
                      <input
                        type="text"
                        defaultValue={tag.attributes[attributeIndex].value}
                        onChange={(e) => handleOnChangeAttributeValue(e, tagIndex, attributeIndex)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      <div className="buttons">
        <input type="button" value="キャンセル" className="btnbg-g" onClick={handleEditAttributeCancelButtonClick} />
        <input type="button" value="適用" onClick={handleEditAttributeApplyButtonClick} disabled={editor.isReadOnly} />
      </div>
    </ReactModal>
  );
}

export { TextAttributeDialog };
